import { logger } from '../utils/logger';
import { api } from './api';

export interface RealAddress {
  line1: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

export interface RealAddressResponse {
  success: boolean;
  address: RealAddress;
  state_used: string;
  source: string;
  error?: string;
}

export const addressService = {
  /**
   * Generate a real address for testing purposes
   * @param state - Optional state code (e.g., 'CA', 'TX')
   * @param preferredZip - Optional preferred ZIP code
   * @param preferredCity - Optional preferred city name
   * @returns Promise with real address data
   */
  generateRealAddress: async (
    state?: string,
    preferredZip?: string,
    preferredCity?: string
  ): Promise<RealAddressResponse> => {
    try {
      const params = new URLSearchParams();
      if (state) params.append('state', state);
      if (preferredZip) params.append('preferred_zip', preferredZip);
      if (preferredCity) params.append('preferred_city', preferredCity);

      const response = await api.get(`/api/v1/tax-testing/generate-real-address?${params.toString()}`);
      return response.data;
    } catch (error) {
      logger.error('Error generating real address:', error);
      // Return a fallback address if the API fails
      return {
        success: false,
        address: {
          line1: '123 Main St',
          city: 'Springfield',
          state: state || 'IL',
          postal_code: '62701',
          country: 'US'
        },
        state_used: state || 'IL',
        source: 'fallback',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * Generate a real address for a specific state
   * @param state - State code (e.g., 'CA', 'TX')
   * @returns Promise with real address data
   */
  generateRealAddressForState: async (state: string): Promise<RealAddressResponse> => {
    return addressService.generateRealAddress(state);
  },

  /**
   * Generate a random real address (no specific state)
   * @returns Promise with real address data
   */
  generateRandomRealAddress: async (): Promise<RealAddressResponse> => {
    return addressService.generateRealAddress();
  }
}; 